import React, { useEffect, useState } from 'react';
import { LeftOutlined, RightOutlined, BuildOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { BodyText, H1, SmallText } from '../../../../components/ui/Typography';
import defaultImage from '../../../../assets/default-image.png';
import AntdCustomButton from '../../../../components/buttons/AntdCustomButton';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import BusinessModel, { IBusiness } from '../../models/BusinessModel';
import { message, Spin } from 'antd';
import { WIZARD_STEPS } from '../../../wizard-steps-description';
import StepperModel from '../../../../models/StepperModel';
import Step from '../../Step';
import ScreenNavigationModel, {
  ScreenType
} from '../../models/ScreenNavigationModel';
import SocialMediaModel from '../../../SocialMedia/models/SocialMediaModel';
import HelpBoxCompanionModel from '../../../../models/HelpBoxCompanionModel';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../../components/animations/StepAnimations';

function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState('desktop');
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 768) setBreakpoint('mobile');
      else if (window.innerWidth < 1024) setBreakpoint('tablet');
      else setBreakpoint('desktop');
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return breakpoint;
}

const SelectYourCompany = observer(() => {
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingBusinessId, setLoadingBusinessId] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [nextScreen, setNextScreen] = useState<ScreenType | null>(null);
  const breakpoint = useBreakpoint();

  const companies = BusinessModel.businessList;
  const source = BusinessModel.businessSearchSource; // 'places' | 'gmb'

  let maxStatic;
  if (breakpoint === 'mobile') maxStatic = companies.length;
  else if (breakpoint === 'tablet') maxStatic = 3;
  else maxStatic = 4;

  const isSlider = companies.length > maxStatic;

  const handleScreenChange = async () => {
    setIsVisible(false);
  };

  const nextSlide = () => {
    if (loadingBusinessId) return;
    setCurrentIndex((prev) => (prev + 1) % companies.length);
  };

  const prevSlide = () => {
    if (loadingBusinessId) return;
    setCurrentIndex((prev) => (prev - 1 + companies.length) % companies.length);
  };

  const getVisibleCompanies = React.useCallback(() => {
    const result = [];
    const totalCompanies = companies.length;
    for (let i = -2; i <= 2; i++) {
      const index = (currentIndex + i + totalCompanies) % totalCompanies;
      result.push({
        ...companies[index],
        position: i
      });
    }
    return result;
  }, [currentIndex, companies]);

  const handleCompanyClick = (company) => {
    const index = companies.findIndex((c) => c.id === company.id);
    setCurrentIndex(index);
  };

  const selectBusiness = React.useCallback(async (business) => {
    if (WIZARD_STEPS[StepperModel.currentStep].step) {
      await (
        WIZARD_STEPS[StepperModel.currentStep].step as Step
      ).selectBusiness(business, business.website, business.domain_type);

      if (BusinessModel.selectedCategoryName) {
        setNextScreen(ScreenType.CORPORATE_CATEGORY);
        await handleScreenChange();
      } else {
        setNextScreen(ScreenType.CHOOSE_CORPORATE_CATEGORY);
        await handleScreenChange();
      }
    }
  }, []);

  const handleCenteredCardClick = React.useCallback(
    async (company: IBusiness) => {

      if (company.website) {
        //first, fire the request to load social media profiles so that it loads by the time the business data is loaded
        // do not wait for the request to finish before proceeding to next step
        SocialMediaModel.loadSocialMediaProfiles({
          location: company.address,
          businessName: company.name,
          website: company.website
        });
      }

      setLoadingBusinessId(company.id);
      try {
        BusinessModel.setBusinessDataLoading(true);
        const business = BusinessModel.getBussinessById(company.id);
        await selectBusiness(business);
        BusinessModel.setBusinessDataLoading(false);
      } catch (e) {
        message.error(t('An error occurred while selecting the business.'));
        console.error(e);
      } finally {
        BusinessModel.setBusinessDataLoading(false);
        setLoadingBusinessId(null);
      }
    },
    [t, selectBusiness]
  );

  const visibleCompanies = isSlider
    ? getVisibleCompanies()
    : companies.map((c, i) => ({ ...c, position: 0 }));

  const helpBoxTitleMessage = t('How do we find your company?');
  const helpBoxParagraphMessage = t('We are looking for your company name or industry at the specified place at Google. If we do not find it, try it with more general - maybe it is different or categorized. Without Google entry you can also enter the data manually.');

  React.useEffect(() => {
    HelpBoxCompanionModel.setDisplayHelpBox(true);
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);

  const phoneHref = (phone: string) => {
    if (!phone) return undefined;
    const cleaned = phone.replace(/[^+\d]/g, '');
    return `tel:${cleaned}`;
  };

  const isMyCompanyNotIncludedLabelVisible =
    !BusinessModel.searchingMoreCompanies &&
    !(BusinessModel.manualGmbSearchTriggered && source === 'gmb');

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (nextScreen) {
          setTimeout(() => {
            ScreenNavigationModel.setCurrentScreen(nextScreen);
          }, 200);
        }
      }}>
      {isVisible && (
        <motion.div
          className={`flex items-center sm:justify-center md:p-4 w-full ${
            isSlider ? 'md:overflow-hidden' : ''
          }`}
          initial="hidden"
          animate="visible"
          variants={staggerContainerVariants}
          exit="hidden"
        >
          <div>
            <motion.div
              variants={fadeInUpVariants}
              className="md:text-center mb-8"
            >
              <H1 className="text-2xl font-bold mb-4">
                {t('Select your company')}
              </H1>
            </motion.div>

            <div className={`relative ${BusinessModel.searchingMoreCompanies ? 'opacity-30 blur-sm pointer-events-none transition' : 'transition'}` }>
              <motion.div
                variants={fadeInUpVariants}
                className={`flex ${breakpoint === 'mobile'
                  ? 'flex-col  sm:items-center'
                  : 'flex-row justify-center items-stretch'
                  } gap-[36px] md:mb-8`}
              >
                {visibleCompanies.map((company) => (
                  <div
                    key={company.id}
                    id={`company-option-${company.id}`}
                    data-ga-event="company-selection"
                    className={`rounded-[16px] cursor-pointer transition-all duration-500 flex flex-col ${company.position === 0 ? 'ring-2 ring-blue-500' : ''
                      } sm:w-[296px] w-full`}
                    onClick={() =>
                      company.position === 0
                        ? handleCenteredCardClick(company)
                        : handleCompanyClick(company)
                    }
                    style={{
                      background:
                        'linear-gradient(94.3deg, #FEFEFE 0%, #FAFAFE 50%, #F8F5FA 100%)',
                      position: 'relative',
                      border: '1px solid #dad7e4',
                      boxShadow: `
                        0 2px 4px 0 rgba(233,234,241,1),
                        0 -2px 2px 0 rgba(233,234,241,1)
                      `,
                      opacity: isSlider
                        ? Math.abs(company.position) === 1
                          ? 0.4
                          : Math.abs(company.position) === 2
                            ? 0.2
                            : 1
                        : 1
                    }}>
                    {loadingBusinessId === company.id && company.position === 0 && (
                      <div className="absolute inset-0 bg-white bg-opacity-70 z-10 rounded-[16px] flex items-center justify-center">
                        <Spin size="small" tip={t('Loading...')} />
                      </div>
                    )}

                    <div className="h-[144px] bg-gradient-to-br rounded-t-[16px] from-gray-100 to-gray-200 flex items-center justify-center">
                      <img
                        src={company.picture[0] || defaultImage}
                        alt={company.name}
                        className="w-full h-full object-cover object-top rounded-t-[16px]"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultImage;
                        }}
                      />
                    </div>
                    <div className="p-[24px] flex flex-col justify-between text-left">
                      <BodyText className="text-lg mb-2 font-bold" style={{ lineHeight: 1.45 }}>
                        {company.name}
                      </BodyText>
                      <div>
                        <SmallText>{company.address}</SmallText>
                        <SmallText>{company.city}</SmallText>
                        <SmallText>{company.country}</SmallText>
                        {source === 'gmb' && company.phoneNumber && (
                          <SmallText>
                            <a
                              href={phoneHref(company.phoneNumber)}
                              className="phone"
                              style={{ color: 'rgba(0, 0, 0, 0.88)', borderBottom: '1px solid rgba(0, 0, 0, 0.88)' }}
                              onClick={(e) => e.stopPropagation()}>
                              {company.phoneNumber}
                            </a>
                          </SmallText>
                        )}
                        <SmallText>{company.website}</SmallText>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>

              {isSlider && (
                <motion.div
                  variants={fadeInUpVariants}
                  className="flex justify-center gap-4 mb-8"
                >
                  <button
                    onClick={prevSlide}
                    className={`slider-button w-11 rounded-full ${loadingBusinessId ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                      } text-white flex items-center justify-center transition focus:outline-none`}
                    style={{
                      width: '44px',
                      height: '44px',
                      border: 'none',
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                    disabled={!!loadingBusinessId}
                    aria-label="Previous">
                    <LeftOutlined className="text-lg" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className={`slider-button w-11 rounded-full ${loadingBusinessId ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                      } text-white flex items-center justify-center transition focus:outline-none`}
                    style={{
                      width: '44px',
                      height: '44px',
                      border: 'none',
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                    disabled={!!loadingBusinessId}
                    aria-label="Next">
                    <RightOutlined className="text-lg" />
                  </button>
                </motion.div>
              )}
            </div>
            <motion.div
              variants={fadeInUpVariants}
              className="flex justify-center mt-4"
            >
              <AntdCustomButton
                {...(isMyCompanyNotIncludedLabelVisible
                  ? {
                      id: 'company-not-listed',
                      'data-ga-event': 'company-not-listed'
                    }
                  : {})}
                appearance="default"
                disabled={!!loadingBusinessId || BusinessModel.searchingMoreCompanies}
                onClick={async () => {
                  if (BusinessModel.searchingMoreCompanies) return;
                  const searchPath = BusinessModel.buildSearchPath(
                    BusinessModel.companyName,
                    BusinessModel.zipOrCity
                  );
                  if (source === 'places') {
                    BusinessModel.setManualGmbSearchTriggered(true);
                    await BusinessModel.findBusinessOnGMB(searchPath);
                    if (BusinessModel.businessList.length === 0) {
                      setNextScreen(ScreenType.COMPANY_NOT_INCLUDED);
                      await handleScreenChange();
                    }
                  } else {
                    setNextScreen(ScreenType.COMPANY_NOT_INCLUDED);
                    await handleScreenChange();
                  }
                }}>
                {BusinessModel.searchingMoreCompanies
                  ? t('Searching for more companies')
                  : BusinessModel.manualGmbSearchTriggered && source === 'gmb'
                    ? t('My company is still not included')
                    : t('My company is not included')}
              </AntdCustomButton>
            </motion.div>
            {BusinessModel.searchingMoreCompanies && (
              <div className="mt-4 flex flex-col items-center gap-2 text-center">
                <Spin size="small" />
                <SmallText>{t('Searching for more companies')}...</SmallText>
              </div>
            )}
            {/* No automatic fallback search on this screen anymore; GMB results arrive only if previous screen failed Places. */}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default SelectYourCompany;
